/**
 * Доступность (a11y) для модальных диалогов:
 *
 * - Стек открытых модалок, чтобы корректно обрабатывать вложенные диалоги
 *   (например, форма примечания поверх списка примечаний).
 * - Глобальный обработчик ESC: закрывает верхний диалог стека через его
 *   "официальную" кнопку закрытия (атрибут `data-modal-close-btn` указывает
 *   id кнопки, чтобы повторно использовать существующие cleanup-обработчики).
 * - Focus trap: Tab / Shift+Tab циклится внутри модалки.
 * - При открытии запоминаем последний активный элемент и возвращаем фокус
 *   на него при закрытии.
 */

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[contenteditable='true']",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

interface ModalEntry {
  overlay: HTMLElement;
  previousFocus: HTMLElement | null;
}

const stack: ModalEntry[] = [];

function getModalElement(overlay: HTMLElement): HTMLElement {
  return overlay.querySelector<HTMLElement>(".modal") ?? overlay;
}

function getFocusables(modal: HTMLElement): HTMLElement[] {
  return Array.from(modal.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) =>
      !el.hasAttribute("disabled") &&
      el.getAttribute("aria-hidden") !== "true" &&
      // offsetParent === null означает display:none у родителя; visibility и
      // прозрачность мы здесь не учитываем — для наших модалок достаточно.
      el.offsetParent !== null,
  );
}

function focusFirstInModal(overlay: HTMLElement): void {
  const modal = getModalElement(overlay);
  // отложенно, чтобы дать браузеру отрисовать display:flex
  window.requestAnimationFrame(() => {
    const focusables = getFocusables(modal);
    const target = focusables[0] ?? modal;
    if (!target.hasAttribute("tabindex") && target === modal) {
      modal.setAttribute("tabindex", "-1");
    }
    try {
      target.focus({ preventScroll: false });
    } catch (_) {
      /* ignore */
    }
  });
}

export function registerModalA11yOpen(overlay: HTMLElement): void {
  const previous =
    document.activeElement instanceof HTMLElement ? document.activeElement : null;
  stack.push({ overlay, previousFocus: previous });
  overlay.setAttribute("role", overlay.getAttribute("role") ?? "dialog");
  overlay.setAttribute("aria-modal", "true");
  const modal = overlay.querySelector<HTMLElement>(".modal");
  if (modal) {
    modal.setAttribute("aria-modal", "true");
    if (!modal.getAttribute("role")) {
      modal.setAttribute("role", "dialog");
    }
  }
  focusFirstInModal(overlay);
}

export function registerModalA11yClose(overlay: HTMLElement): void {
  const index = stack.findIndex((entry) => entry.overlay === overlay);
  if (index === -1) {
    return;
  }
  const [removed] = stack.splice(index, 1);
  overlay.removeAttribute("aria-modal");
  if (removed?.previousFocus) {
    try {
      removed.previousFocus.focus();
    } catch (_) {
      /* ignore */
    }
  }
}

function attemptCloseTopModal(): boolean {
  const top = stack[stack.length - 1];
  if (!top) {
    return false;
  }
  const closeBtnId = top.overlay.dataset.modalCloseBtn;
  if (closeBtnId) {
    const btn = document.getElementById(closeBtnId);
    if (btn instanceof HTMLElement) {
      btn.click();
      return true;
    }
  }
  // запасной путь — просто скрыть оверлей
  top.overlay.classList.remove("is-open");
  top.overlay.setAttribute("aria-hidden", "true");
  registerModalA11yClose(top.overlay);
  return true;
}

document.addEventListener(
  "keydown",
  (event: KeyboardEvent) => {
    if (stack.length === 0) {
      return;
    }
    const top = stack[stack.length - 1];
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      attemptCloseTopModal();
      return;
    }
    if (event.key === "Tab") {
      const modal = getModalElement(top.overlay);
      const focusables = getFocusables(modal);
      if (focusables.length === 0) {
        event.preventDefault();
        modal.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (event.shiftKey) {
        if (active === first || !modal.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (active === last || !modal.contains(active)) {
          event.preventDefault();
          first.focus();
        }
      }
    }
  },
  true,
);
